import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, OnDestroy, NgZone, ChangeDetectorRef, Self } from '@angular/core';
import { TreeNode, MenuItem } from 'primeng/api';
import { FolderTreeNode } from '../../typescript-angular-client/model/folderTreeNode';
import { DragAndDropEvent, FileModel } from '../../typescript-angular-client/model/fileModel';
import { TreeViewService } from '../../typescript-angular-client/api/treeView.service';
import { finalize, map, filter, tap, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { DialogService, IDialogHandle } from '../../dialogs/dialog.service';
import { DialogCloseReason, IDialogOnClose } from '../../dialogs/base-dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { FmTreeService } from '../services/fm-tree.service';
import { NgOnDestroyService } from '../services/ng-on-destroy.service';
import { ErrorHandlingService } from '../../http/error-handling.service';

@Component({
  selector: 'fm-folders-tree',
  templateUrl: './fm-folders-tree.component.html',
  styleUrls: ['./fm-folders-tree.component.scss'],
  providers: [NgOnDestroyService]
})
export class FmFoldersTreeComponent implements OnInit, OnDestroy {
  private selectedNode: FolderTreeNode;
  private currentSelectedNode: FolderTreeNode = null;
  private previosSelectedNode: FolderTreeNode;
  private expandedNodeList: string[];
  private subscriptions: Subscription[] = [];

  folderDrag: FolderTreeNode = null;
  draggedItem: FileModel | FileModel[] = null;
  loadedNodes: TreeNode[] = [];
  loading = false;
  menuItems: MenuItem[] = [
    {
      label: 'New',
      icon: 'pi pi-fw pi-plus',
      command: () => this.onAddNewFolder()
    },
    {
      label: 'Rename',
      icon: 'pi pi-fw pi-pencil',
      command: () => this.RenameFolderCommand(),
    },
    { label: 'Refresh', icon: 'pi pi-fw pi-refresh', command: () => this.RefreshCommand() },
    { label: 'Delete', icon: 'pi pi-fw pi-trash', command: () => this.DeleteFolderCommand() }
  ];

  @Input()
  dialogType = FileModel.FileTypeEnum.IMAGES;

  @Input() showLoader = false;

  @Output() nodeSelected = new EventEmitter<FolderTreeNode>(null);
  @Output() nodeToDrop = new EventEmitter<unknown>();
  @Output() nodeToDrag = new EventEmitter<FolderTreeNode>(null);

  constructor(
    private treeViewService: TreeViewService,
    private dialogService: DialogService,
    private translate: TranslateService,
    private fmTreeService: FmTreeService,
    private zone: NgZone,
    private cd: ChangeDetectorRef,
    @Self() private ngOnDestroy$: NgOnDestroyService,
    private errHandler: ErrorHandlingService
  ) {
    this.treeViewService.defaultHeaders = this.treeViewService.defaultHeaders.set('api-version', '2.0');
  }

  ngOnInit() {
    this.tryGetAllFolders();

    this.fmTreeService.onStartDrag()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((items) => this.draggedItem = items);

    this.fmTreeService.onStoptDrag()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.draggedItem = null);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
    this.folderDrag = null;
    this.loadedNodes = [];
    this.loading = false;
    this.SelectedNode = null;
  }

  public tryGetAllFolders = (oldNode: FolderTreeNode = null, expandPreviosTree = false): void => {
    this.loading = this.showLoader;
    if (expandPreviosTree) {
      this.expandedNodeList = this.getExpandedNode(this.loadedNodes[0], new Array());
    }
    this.treeViewService.treeViewGetAllFoldersV2(this.dialogType)
      .pipe(
        map((res: FolderTreeNode) => this.convertFolder(res)),
        catchError((err) => this.errHandler.handleError(err)),
        finalize(() => this.zone.run(() => {
          this.loading = false;
          this.cd.detectChanges();
        })),
        takeUntil(this.ngOnDestroy$))
      .subscribe((res: TreeNode) => {
        this.zone.run(() => {
          this.loadedNodes = [res];
          this.trySetPreviosSelectedNode(oldNode);
        });
      });
  }

  public getFolderParents = (nodeId: string): string[] => {
    const flatten = (children, extractChildren, level, parent) => Array.prototype.concat.apply(
      children.map(x => ({ ...x, level: level || 1, parent: parent || null })),
      children.map(x => flatten(extractChildren(x) || [], extractChildren, (level || 1) + 1, x.key)));

    const flat = flatten(this.loadedNodes, x => x.children, 0, this.loadedNodes[0].key).map(x => delete x.children && x);

    let foundNode = flat.find(x => x.key === nodeId);

    const pathToNode = new Array();

    if (pathToNode == null) {
      return pathToNode;
    }

    pathToNode.push(foundNode);

    while (foundNode.key !== '0') {
      foundNode = flat.find(x => x.key === foundNode.parent);
      pathToNode.push(foundNode.key);
    }

    return pathToNode;
  }

  private trySetPreviosSelectedNode = (oldNode: FolderTreeNode): void => {
    this.loadedNodes[0].expanded = true;

    if (oldNode == null) {
      this.SelectedNode = this.loadedNodes[0];
      return;
    }

    const flatten = (children, extractChildren, level, parent) => Array.prototype.concat.apply(
      children.map(x => ({ ...x, level: level || 1, parent: parent || null })),
      children.map(x => flatten(extractChildren(x) || [], extractChildren, (level || 1) + 1, x.key)));

    const pathToNode = this.getFolderParents(oldNode.key);

    //item was deleted
    if (pathToNode.length === 0) {
      this.SelectedNode = this.loadedNodes[0];
      return;
    }

    const reversedArray = pathToNode.reverse();
    let expandAllArray = new Array();

    if (this.expandedNodeList.length > 0) {
      expandAllArray = reversedArray.concat(this.expandedNodeList.filter((item) => reversedArray.indexOf(item) < 0));
    } else {
      expandAllArray = reversedArray;
    }

    this.expandNodes(this.loadedNodes[0], expandAllArray);
    this.SelectedNode = oldNode;
  }

  public onNodeDrop = (
    event: DragEvent & { dropNode: FolderTreeNode; dragNode: FolderTreeNode; accept: () => void }
  ): void => {
    const dropNode = event.dropNode;

    if (this.draggedItem) {
      const droppedItem: FileModel = { IsFolder: true, Id: dropNode.key };
      const emitEntity: DragAndDropEvent = {
        originalEvent: event,
        isFolderTreeDrop: true,
        draggedItem: this.draggedItem,
        droppedItem: droppedItem
      };
      this.nodeToDrop.emit(emitEntity);
      return;
    }

    const dragNode = event.dragNode;
    const parenId = dropNode.key ? dropNode.key : '0';
    this.loading = true;
    const s = this.treeViewService.treeViewUpdateFolder({ id: dragNode.key, parentId: parenId, folderName: dragNode.label }, this.dialogType)
      .pipe(tap(() => event.accept()),
        finalize(() => this.loading = false))
      .subscribe({
        next: () => {
          dragNode.data.ParentFolderId = parenId;
          dragNode.parent = dropNode;
        },
        error: () => this.RefreshCommand()
      });
    this.subscriptions.push(s);
  }

  public nodeSelect = (e): void => {}

  public onDragStart = (event: DragEvent, node: FolderTreeNode): void => this.nodeToDrag.emit(node);

  get SelectedNode() {
    return this.selectedNode;
  }

  set SelectedNode(node: FolderTreeNode) {
    this.selectedNode = node;
    const castedUiNode = node as TreeNode;
    if (castedUiNode && !castedUiNode.expanded && castedUiNode.children) {
      castedUiNode.expanded = true;
    }

    this.nodeSelected.emit(node);
  }

  private convertFolder(folder: TreeNode) {
    folder.key = folder.key ? folder.key : '0';
    folder.draggable = true;
    folder.droppable = true;
    folder.expandedIcon = 'folder-icon';
    folder.collapsedIcon = 'folder-icon';
    folder.children.forEach((n) => this.convertFolder(n));
    return folder;
  }

  RefreshCommand() {
    this.previosSelectedNode = this.SelectedNode;
    this.expandedNodeList = this.getExpandedNode(this.loadedNodes[0], new Array());
    this.loadedNodes = [];
    this.SelectedNode = null;
    this.tryGetAllFolders(this.previosSelectedNode);
  }

  onAddNewFolder() {
    let folderName = this.translate.instant('COMMON.DIALOGS.CREATE_NEW_FOLDER.NEW_FOLDER_NAME');
    const dialogHandle: IDialogHandle = this.dialogService.input({
      header: this.translate.instant('COMMON.DIALOGS.CREATE_NEW_FOLDER.CREATE_NEW_FOLDER'),
      fieldName: this.translate.instant('COMMON.DIALOGS.CREATE_NEW_FOLDER.FOLDER_NAME'),
      fieldDefaultValue: folderName,
      okCaption: this.translate.instant('COMMON.DIALOGS.CREATE_NEW_FOLDER.CREATE'),
      cancelCaption: this.translate.instant('COMMON.DIALOGS.CREATE_NEW_FOLDER.CANCEL'),
      fieldValidationContext: {
        selectedFolderNode: this.selectedNode
      },
      fieldValidationCallback: (v) => {
        folderName = v;
        return this.folderNameValidation(v);
      }
    });

    const s = dialogHandle.onClose
      .pipe(
        filter((v: IDialogOnClose) => v.reason === DialogCloseReason.OK),
        switchMap(() => this.treeViewService.treeViewAddFolder({
          parentId: this.selectedNode.key ? this.selectedNode.key : '0',
          folderName: folderName.trim()
        }, this.dialogType)),
        tap(() => this.RefreshCommand()))
      .subscribe();
    this.subscriptions.push(s);
  }

  RenameFolderCommand() {
    if (!this.selectedNode.data || !this.selectedNode.data.ParentFolderId) {
      return;
    }
    let folderName = '';
    const dialogHandle: IDialogHandle = this.dialogService.input({
      fieldDefaultValue: this.selectedNode.label,
      header: this.translate.instant('COMMON.DIALOGS.RENAME_FOLDER.RENAME_FOLDER'),
      fieldName: this.translate.instant('COMMON.DIALOGS.RENAME_FOLDER.FOLDER_NAME'),
      okCaption: this.translate.instant('COMMON.DIALOGS.RENAME_FOLDER.RENAME'),
      cancelCaption: this.translate.instant('COMMON.DIALOGS.RENAME_FOLDER.CANCEL'),
      fieldValidationContext: {
        selectedFolderNode: this.selectedNode
      },
      fieldValidationCallback: (v, ctx) => {
        folderName = v;
        return this.folderNameValidation(v);
      }
    });

    const parentFolderId = this.selectedNode.data.ParentFolderId;
    const s = dialogHandle.onClose
      .pipe(
        filter((v: IDialogOnClose) => v.reason === DialogCloseReason.OK),
        switchMap(() => this.treeViewService.treeViewUpdateFolder(
      {
        id: this.selectedNode.key,
        parentId: parentFolderId,
        folderName: folderName.trim()
      }, this.dialogType)),
      tap(() => this.RefreshCommand()))
  .subscribe();
    this.subscriptions.push(s);
  }

  private DeleteFolderCommand() {
    if (this.SelectedNode) {
      const s = this.dialogService.confirm({
        header: this.translate.instant('COMMON.DIALOGS.DELETE_FOLDER.DELETE_FOLDER'),
        message: this.translate.instant('COMMON.DIALOGS.DELETE_FOLDER.MESSAGE'),
        okCaption: this.translate.instant('COMMON.DIALOGS.DELETE_FOLDER.DELETE'),
        cancelCaption: this.translate.instant('COMMON.DIALOGS.DELETE_FOLDER.CANCEL')
      })
        .onClose
        .pipe(
          filter((r: IDialogOnClose) => r.reason === DialogCloseReason.OK),
          switchMap(() => this.treeViewService.treeViewDeleteFolder(this.SelectedNode.key)))
        .subscribe({
          next: () => {
            this.SelectedNode = this.SelectedNode.parent;
            this.RefreshCommand();
            },
          error: () => {
          }
        });
      this.subscriptions.push(s);
    }
  }

  setItems() {
    //rename
    this.menuItems[1].disabled = this.selectedNode.key === '0';
    //delete
    this.menuItems[3].disabled = this.selectedNode.key === '0';
  }

  private expandNodes(node: TreeNode, nodeKeyList: string[]) {
    node.expanded = true;
    if (node.children) {
      for (let i = 0; i < node.children.length; i++) {
        if (nodeKeyList.includes(node.children[i].key)) {
          this.expandNodes(node.children[i], nodeKeyList.filter(k => k !== node.children[i].key));
        }
      }
    }
  }

  private getExpandedNode(node: TreeNode, selectedNodeList: string[]): string[] {
    if (node && node.expanded) {
      selectedNodeList.push(node.key);
      if (node.children) {
        node.children.forEach(n => this.getExpandedNode(n, selectedNodeList));
      }
    }

    return selectedNodeList;
  }
  
  private findTreeNode(currentNode: FolderTreeNode) {
    if (currentNode.key === this.selectedNode.key) {
      this.currentSelectedNode = currentNode;
      return;
    }

    for (const node of currentNode.children) {
      if (node.key === this.selectedNode.key) {
        this.currentSelectedNode = node;
        break;
      } else {
        this.findTreeNode(node);
      }
    }
  }

  private folderNameValidation(newFolderName: string) {
    // trim it to prevent spaces in the beginning and the end to differentiate the folder name.
    newFolderName = newFolderName.trim();

    if (newFolderName.length < 3 || newFolderName.length > 255) {
      return this.translate.instant('COMMON.DIALOGS.CREATE_NEW_FOLDER.NAME_LENGTH_VALIDATION');
    }

    for (const node of this.loadedNodes) {
      this.findTreeNode(node);
      if (this.currentSelectedNode !== null) {
        break;
      }
    }

    if (this.currentSelectedNode && this.currentSelectedNode.children) {
      const isExists =
        this.currentSelectedNode.children.find(i => i.label.toLowerCase() === newFolderName.toLowerCase()) != null;
        if (isExists) {
          this.currentSelectedNode = null;
        return this.translate.instant('COMMON.DIALOGS.CREATE_NEW_FOLDER.FOLDER_ALREADY_EXISTS_VALIDATION'); // FOLDER_ALREADY_EXISTS_VALIDATION
      }
    }
    return null;
  }
}
